import { z } from "zod";

import { VALUE_KINDS, type ProjectedFieldValue } from "./field-values";

/**
 * Wire shapes for a single field value. The serialised `value` shape on the
 * wire differs from the in-memory
 * `ProjectedFieldValue` discriminated union. Callers that already have
 * a `ProjectedFieldValue` (e.g. the read helper in M3) should pass it
 * through `apiValueFromProjected` to obtain the on-wire shape.
 */

/**
 * Currency values serialise as `{ amount, currency_code }` on the wire.
 * `amount` is in the same minor units as `field_values.value_numeric`
 * for currency rows (an integer).
 */
export const ApiCurrencyValueSchema = z.object({
  amount: z.number().int(),
  currency_code: z.string().length(3),
});

export type ApiCurrencyValue = z.infer<typeof ApiCurrencyValueSchema>;

/**
 * Wire shape of the `value` field on each `values[]` entry. The union
 * here is flat — the receiving client dispatches on the entry's
 * `value_kind` field (a closed enum carried alongside `value`) rather
 * than re-parsing the value shape. Each variant is a leaf type so no
 * extra wrapper objects sneak in.
 *
 *   string / enum  -> string
 *   number         -> number
 *   currency       -> { amount, currency_code }
 *   date           -> string ("YYYY-MM-DD")
 *   datetime       -> string (ISO 8601 / RFC 3339 with offset)
 *   boolean        -> boolean
 *   (unset)        -> null
 */
export const ApiFieldValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  ApiCurrencyValueSchema,
  z.null(),
]);

export type ApiFieldValue = z.infer<typeof ApiFieldValueSchema>;

/**
 * One entry of the `values[]` array on a canonical record response.
 *
 * - `field_definition_id` is `null` for native attributes and the field
 *   definition's `id` for custom values.
 * - `field_path` is the durable, four-segment custom path
 *   (`$.<owner>.custom.<namespace>.<key>`) for customs, and the
 *   native two-segment path (`$.<owner>.<leaf>`) for natives. The path
 *   format is validated downstream by the natives registry / write
 *   router (M3); this schema only carries the string.
 * - `value_kind` is the closed enum of value kinds (`string`, `enum`,
 *   `number`, `currency`, `date`, `datetime`, `boolean`). Consumers
 *   dispatch rendering / parsing off this field rather than sniffing
 *   the runtime shape of `value`. Always populated, even when `value`
 *   is `null` (so the renderer can pick a kind-appropriate placeholder).
 * - `value` is the wire-shape value; see `ApiFieldValueSchema`.
 * - `decision_id` is reserved for the future provenance layer. Always
 *   `null` in v1 — no writer in this milestone populates it.
 */
export const FieldValueEntrySchema = z.object({
  field_definition_id: z.string().nullable(),
  field_path: z.string(),
  value_kind: z.enum(VALUE_KINDS),
  value: ApiFieldValueSchema,
  decision_id: z.string().nullable(),
});

export type FieldValueEntry = z.infer<typeof FieldValueEntrySchema>;

/**
 * Convert an in-memory `ProjectedFieldValue` to the wire-shape `value`
 * field used in `FieldValueEntrySchema`. Total over `ValueKind`; an
 * unknown kind throws (the `ProjectedFieldValueSchema` is the closed
 * source of truth for what's legal).
 *
 * This is the only place the in-memory <-> wire shape mapping lives,
 * so M3's read helper and any future serialisation code can call this
 * directly rather than reimplementing the per-kind shape.
 */
export function apiValueFromProjected(input: ProjectedFieldValue): ApiFieldValue {
  switch (input.valueKind) {
    case "string":
    case "enum":
      return input.valueText;
    case "number":
      return input.valueNumeric;
    case "currency":
      return { amount: input.valueNumeric, currency_code: input.valueCurrencyCode };
    case "date":
      return input.valueDate;
    case "datetime":
      return input.valueDatetime;
    case "boolean":
      return input.valueBool;
    default: {
      const exhaustive: never = input;
      throw new Error(
        `apiValueFromProjected: unrecognised valueKind ${JSON.stringify(
          (exhaustive as { valueKind?: unknown } | undefined)?.valueKind,
        )}`,
      );
    }
  }
}
